<template>
  <div>
    <section class="mt-2">
      <div>
        <steps :step="step" v-on:step-change="onStepChange"></steps>
      </div>
    </section>

    <section
      aria-labelledby="invoicing-address-heading"
      class="mt-10"
      v-show="step === 'billingAddress'"
    >
      <div class="flex mt-2">
        <contact :contact="invoiceContact" v-on:step-change="onStepChange" ref="invoiceContactComponent">
          <h2 class="mb-6 text-center" id="invoicing-address-heading">
            - {{ $t("shop.invoicing-info") }} -
          </h2>
          <template v-slot:nocaptcha>
            <slot name="nocaptcha"></slot>
          </template>
        </contact>
      </div>
    </section>

    <section
      aria-labelledby="shipping-address-heading"
      class="mt-10"
      v-show="step === 'shippingAddress'"
    >
      <div class="flex mt-2">
        <contact :contact="shippingContact" v-on:step-change="onStepChange"  ref="shippingContactComponent">
          <h2 class="mb-6 text-center" id="shipping-address-heading">
            - {{ $t("shop.shipping-address") }} -
          </h2>
        </contact>
      </div>
    </section>

    <section
      aria-labelledby="shipping-method-heading"
      class="mt-10"
      v-show="step === 'shippingMethod'"
    >
      <div class="mt-2">
        <shipping
          :shipping-items="shippingItems"
          v-on:step-change="onStepChange"
          :shipping-costs="shippingCostsModel"
        >
        </shipping>
      </div>
    </section>

    <section
      aria-labelledby="payment-method-heading"
      class="mt-10"
      v-if="step === 'paymentMethod'"
    >
      <div class="mt-2">
        <payment :payment-items="payment" v-on:step-change="onStepChange">
        </payment>
      </div>
    </section>

    <div v-show="step === 'orderSummary'">
      <h2 id="summary-heading" class="my-6 text-center">
        - {{ $t("order.summary") }} -
      </h2>

      <div class="mt-6">
        <dl class="sm:flex sm:justify-between text-sm">
          <div class="mb-6">
            <dt class="font-medium text-gray-900">
              {{ $t("shop.shipping-address") }}
            </dt>
            <dd class="mt-3 text-gray-500">
              <span class="block mb-3"
                ><i class="fa fa-user w-5" aria-hidden="true"></i>
                {{ shippingContact.last_name }}
                {{ shippingContact.first_name }}</span>
              <span class="block mb-3"
                ><i class="fas fa-city w-5"></i> {{ shippingContact.postcode }},
                {{ shippingContact.city }},
                {{ shippingContact.country_code }}</span>
              <span class="block mb-3"
                ><i class="fas fa-map-marker-alt w-5"></i>
                {{ shippingContact.address }}</span>
              <span class="block sm:hidden mb-3"
                ><i class="fas fa-envelope-square w-5"></i>
                {{ shippingContact.email }}</span>
              <span class="block sm:hidden"
                ><i class="fas fa-phone w-5"></i>
                {{ shippingContact.phone_number }}</span>
            </dd>
          </div>

          <div class="mb-6 hidden sm:block">
            <dt class="font-medium text-gray-900">
              {{ $t("shop.shipping-updates") }}
            </dt>
            <dd class="mt-3 text-gray-500 space-y-3">
              <p class="mb-3">
                <i class="fas fa-envelope-square w-5"></i>
                <span>{{ shippingContact.email }}</span>
              </p>
              <p>
                <i class="fas fa-phone w-5"></i>
                <span>{{ shippingContact.phone_number }}</span>
              </p>
            </dd>
          </div>
        </dl>
      </div>

      <slot name="summary"></slot>

      <div
        class="
          border-b border-gray-200
          divide-y divide-gray-200
          mb-6
          pb-6
          sm:flex sm:justify-between
        "
      >
        <span class="font-medium text-gray-900"
          >{{ $t("shop.paymentMethod") }}:</span
        >
        <span
          class="sm:text-right text-gray-500"
          v-for="method in payment.methods"
          :key="method.uuid"
          v-if="payment.selected === method.uuid"
        >
          {{ method.name }}
        </span>
      </div>

      <div class="flex justify-end items-center ">
        <checkbox-input
          v-model="subscribeNewsletter" 
          :label="$t('shop.newsletter')" 
        />&nbsp;&nbsp;&nbsp;
      </div>

      <div class="flex justify-end mb-2  items-center ">
        <checkbox-input
          v-model="acceptTerms"
          :errors="errors.acceptTerms"
          :label="$t('shop.accept')"
        />&nbsp;&nbsp;&nbsp;
        <a v-if="hasSkus()"
            class="mb-2 italic hover:underline"
            target="_blank"
            rel="noopener"
            :href="'/shop/' + shippingItems[0].sku.uuid + '/return-policy'"
            >{{ $t('common.terms-and-conditions') }}
          </a>
      </div>

      <div class="sm:flex sm:items-center sm:justify-between">
        <a
          href="/shop/cart"
          class="
            block
            sm:flex sm:items-center
            px-4
            py-3
            rounded
            font-bold
            text-sm
            bg-green-500
            text-white
            hover:bg-green-400
            text-center
            w-full
            sm:w-auto
          "
        >
          <i class="fa fa-shopping-cart" aria-hidden="true"></i>&nbsp;{{
            $t("cart.review-cart")
          }}
        </a>

        <div>
          <div v-if="paymentMethod === 'netopia'">
            <form ref="netopiaPaymentRedirect" method="post" :action="mobilPay.formAction">
              <input type="hidden" name="env_key" :value="mobilPay.formFieelds.env_key" />
              <input type="hidden" name="data" :value="mobilPay.formFieelds.data" />
            </form>

            <button type="submit" 
                  dsav-show="!loading" 
                  @click="loadData();"
                  class="
                      block
                      sm:flex sm:items-center
                      px-4
                      py-3
                      rounded
                      font-bold
                      text-sm
                      cursor-pointer
                      bg-green-500
                      text-white
                      hover:bg-green-400
                      text-center
                      w-full
                      sm:w-auto
                      mt-4
                      sm:mt-0
                    ">
                <span>
                  <i class="fas fa-credit-card"></i>&nbsp;{{ $t("common.pay-now") }}
                </span>
              </button>
          </div>
         
          <!-- <div class="mt-4 sm:mt-0 flex justify-end"> -->
          <button
            @click="finishOrder()"
            type="submit"
            v-if="paymentMethod !== 'netopia'"
            class="
              block
              sm:flex sm:items-center
              px-4
              py-3
              rounded
              font-bold
              text-sm
              cursor-pointer
              bg-green-500
              text-white
              hover:bg-green-400
              text-center
              w-full
              sm:w-auto
              mt-4
              sm:mt-0
            "
          >
            <span v-if="'cod' !== paymentMethod && 'wire' !== paymentMethod">
              <i class="fas fa-credit-card"></i>&nbsp;{{ $t("common.pay-now") }}
            </span>

            <span v-if="'cod' === paymentMethod || 'wire' === paymentMethod">
              {{ $t("order.finish_order") }}
            </span>
          </button>
          <!-- </div> -->

        </div>
      </div>
    </div>
  </div>
</template>


<script>
import CheckboxInput from "@/Shared/CheckboxInput";
import axios from "axios";

export default {
  components: {
    CheckboxInput
  },
  props: {
    initialStep: {
      type: String,
      default: "billingAddress",
    },
    invoiceContact: Object,
    shippingContact: Object,
    shippingItems: Array,
    shippingCosts: Object,
    payment: Object,
  },
  mounted(){
    // alert(1)
  },
  data() {
    return {
      step: this.initialStep,
      paymentMethod: this.payment.selected,
      acceptTerms: false,
      subscribeNewsletter: true,
      errors: {},
      shippingCostsModel : this.shippingCosts,
      loading: true,
      mobilPay:{
        formAction: '',
        formFieelds:{
          env_key: '',
          data: ''
        }
      }
    };
  },
  methods: {
    hasSkus(){
      let has = false;
      if(typeof this.shippingItems[0]!='undefined'){
        if(typeof this.shippingItems[0].sku!='undefined'){
          has = true;
        }
      }
      return has;
    },
    finishOrder() {
      this.errors = {};
      if (this.acceptTerms) {
        let location  = "/shop/pay";
        if(this.subscribeNewsletter){
          location += '?newsletter=1'
        }
        window.location = location;
      } else {
        this.errors = { acceptTerms: [this.$t("common.error-terms")] };
      }
    },

    onStepChange(data) {
      if(data.shippingContact){
        this.$refs.shippingContactComponent.setForm(data.shippingContact);
      }
      if (data.shippingCosts) {
        this.shippingCostsModel = data.shippingCosts;
      }
      if (data.step) {
        if ("shippingMethod" === data.step) {
          //update shipping price
        }
        this.step = data.step;
      }
      if (data.paymentMethod) {
        this.paymentMethod = data.paymentMethod;
      }
    },
    async loadData() {
      if(! this.acceptTerms) {
        this.errors = { acceptTerms: [this.$t("common.error-terms")] };
        return;
      }

      // var url = this.route('payment.netopia.card-redirect-form');
      var url = '/shop/payment/netopia/card-redirect-form';
      if(this.subscribeNewsletter){
        url += '?newsletter=1'
      }
      
      await axios({
          method: "get",
          url: url,
        }).then(
          (response) => {
            //success
            if(response.data instanceof Object && !response.data.error){
              this.mobilPay = response.data;
              var form = this.$refs.netopiaPaymentRedirect;
              setTimeout(function(){
                if(form.action){
                  form.submit()
                }
              },10);

            }else{
              // console.error(response.data.error);
              this.$toastr.e('', this.$t("common.save-error-title"));
            }
            this.loading = false;
          },
          (error) => {
            if (typeof error.response !== "undefined") {
              if (error.response.data) {
                if (error.response.data.errors) {
                  // component.errors = error.response.data.errors;
                  console.error(error.response.data.errors);
                }

                if (error.response.data.message) {
                  console.error(error.response.data.message);
                }
              }
            }
            this.$toastr.e(error.message, this.$t("common.save-error-title"));
          }
        );

    }
  }
};
</script>
